<template lang="pug">
.card.max-w-350.bg-white.shadow-sm
  .card-body.p-4
    el-form(
      ref="formRef"
      :model="item"
      label-position="top"
      :rules="rules"
      class="password-form"
      :hide-required-asterisk="false"
      required
    )
      el-form-item(
        label="Current Password" 
        prop="oldPass"
        class="form-group"
        required
      )
        el-input(
          v-model="item.oldPass"
          placeholder="Current Password"
          type="password"
        )

      el-form-item(
        label="New Password" 
        prop="newPass"
        class="form-group"
        required
      ).el-hint-text-relative
        el-input(
          v-model="item.newPass"
          placeholder="New Password"
          type="password"
        )

      el-form-item(
        label="Repeat New Password" 
        prop="confirmPass"
        class="form-group"
        required
      ).mb-0
        el-input(
          v-model="item.confirmPass"
          placeholder="Repeat New Password"
          type="password"
        )

  .card-footer.bg-white.border-top.p-3.d-flex.justify-content-end
    el-button(
      type="success" 
      @click="validateForm"
    ) Change Password
</template>

<script>
import { defineComponent, reactive, ref } from 'vue';
import { changePassword } from '@/graphs/gql';
import { ElMessage } from 'element-plus';
import shared from '../shared';

export default defineComponent({
  name: 'ChangePassword',

  setup() {
    const formRef = ref(null);
    const { makeGraphRequest, getAuthToken } = shared();

    const item = reactive({
      email: '',
      oldPass: '',
      newPass: '',
      confirmPass: '',
    });

    const validatePass2 = (_, value, callback) => {
      if (value === '') {
        callback(new Error('Please enter confirm password'));
      } else if (value !== item.newPass) {
        callback(new Error('Password and confirm password don\'t match!'));
      } else {
        callback();
      }
    };

    const validatePass = (_, value, callback) => {
      if (value === '') {
        callback(new Error('Please enter new password'));
      } else if (
        value.length < 8 || 
        value.search(/[a-z]/) < 0 || 
        value.search(/[0-9]/) < 0 ||
        value.search(/[A-Z]/) < 0 || 
        value.search(/[!@#$%^&*_-]/) < 0
      ) {
        callback(new Error(
          'Your password must be at least 8 characters in length and contain at least one special character, one lower case letter and one upper case letter.'
        ));
      } else {
        callback();
      }
    };

    const rules = {
      oldPass: { 
        required: true, 
        message: 'Current password is required', 
        trigger: 'blur' 
      },
      newPass: [
        { validator: validatePass, trigger: 'blur' },
      ],
      confirmPass: [
        { validator: validatePass2, trigger: 'blur' },
      ],
    };

    async function submitForm() {
      try {
        await makeGraphRequest(
          'mutation',
          changePassword,
          {
            oldPassword: item.oldPass,
            password: item.newPass,
          },
          getAuthToken()
        );

        ElMessage.info({
          typ: 'success',
          message: 'Password changed successfully',
        });
      } catch (err) {
        ElMessage({
          typ: 'error',
          message: 'Password update failed, please confirm your old password',
        });
      }
    }

    function validateForm() {
      formRef.value.validate((valid) => {
        if (valid) {
          return submitForm();
        }
        return false;
      });
    }

    return {
      rules,
      formRef,
      item,
      validateForm,
    };
  },
});
</script>

<style lang="scss" scoped>
.card {
  background-color: #fff;
  border-radius: 4px;
  border: none;
}

.max-w-350 {
  max-width: 350px;
  margin: 0 auto;
}

.card-footer {
  background-color: #fff;
}

.password-form {
  :deep(.el-form-item) {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(.el-form-item__label) {
    &::before {
      content: '*';
      color: #ff4949;
      margin-right: 4px;
    }
  }
}

:deep(.form-group) {
  .el-form-item__label {
    padding-bottom: 8px;
  }

  .el-form-item__error {
    position: absolute;
    min-height: 20px;
    padding-top: 4px;
  }
}

:deep(.el-form-item__content) {
  min-height: 40px;
}

:deep(.el-input) {
  margin-bottom: 4px;
}
</style>
